/**
 * "Did you mean ...?" for the signup email (REQ-5).
 *
 * A student who registers as name@gmial.com never receives their verification
 * code, booking confirmation or lesson links. The form offers the corrected
 * address before submitting, and the backend refuses the same misspellings
 * (app/Rules/SignupEmailAddress.php in lms-v2-be) - keep the two lists in
 * step.
 */

/** Misspelt domain -> the domain it is almost certainly meant to be. */
export const MISTYPED_EMAIL_DOMAINS: Readonly<Record<string, string>> = {
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmal.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmail.con": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotmail.co": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "yahooo.com": "yahoo.com",
  "yaho.com": "yahoo.com",
  "yahoo.co": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "outlok.com": "outlook.com",
  "outlook.co": "outlook.com",
  "icloud.co": "icloud.com",
};

/**
 * local@domain.tld - one @, no whitespace, and a domain with a dot and an
 * alphabetic top-level domain of at least two letters. Mirrors the backend's
 * SignupEmailAddress pattern so the form rejects exactly what the API would.
 */
export const SIGNUP_EMAIL_PATTERN =
  /^[^\s@]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

export function isValidSignupEmail(email: string): boolean {
  return SIGNUP_EMAIL_PATTERN.test(email.trim());
}

/** The corrected address for a known misspelt domain, else null. */
export function suggestEmailCorrection(email: string): string | null {
  const value = email.trim();
  const at = value.lastIndexOf("@");

  if (at <= 0 || at === value.length - 1) {
    return null;
  }

  const corrected = MISTYPED_EMAIL_DOMAINS[value.slice(at + 1).toLowerCase()];

  return corrected ? `${value.slice(0, at + 1)}${corrected}` : null;
}
